/**
 * Turns a raw look delta in screen pixels into a rotation in radians, applying the
 * player's sensitivity and inversion settings.
 *
 * Pure, and shared by every producer — mouse and touch differ in how they *gather* a
 * delta, not in how a delta becomes a rotation. Keeping this in one place is what makes
 * the sensitivity setting mean the same thing on both.
 */

import type { Settings } from '../game/settings';
import type { Vec2 } from './inputState';

/**
 * Radians of rotation per pixel of input at sensitivity 1. Chosen so a comfortable mouse
 * sweep turns most of the way round; tuned by feel on a device, not derived.
 */
export const RADIANS_PER_PIXEL = 0.004;

/**
 * How much faster a thumb turns than a mouse, per pixel.
 *
 * The one place the two inputs genuinely differ: a mouse can be swept, lifted and swept
 * again, so its travel is unbounded, while a thumb runs out of screen. Without this, a
 * phone player needs four swipes to turn around. Applied by the touch producer as it
 * accumulates, so `applyLookSensitivity` — and the player's sensitivity setting — keep
 * meaning one thing for both schemes.
 */
export const TOUCH_LOOK_GAIN = 2;

export function applyLookSensitivity(rawDeltaPx: Vec2, settings: Settings): Vec2 {
  const scale = RADIANS_PER_PIXEL * settings.lookSensitivity;

  return {
    x: rawDeltaPx.x * scale,
    y: rawDeltaPx.y * scale * (settings.invertLookY ? -1 : 1),
  };
}
